import { _notNil } from '@/littledash';
import cn from 'classnames';
import type { FC, InputHTMLAttributes, Ref } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useFormContext, useFormState } from 'react-hook-form';

type FormInputProps = InputHTMLAttributes<HTMLInputElement> & {
  name: string;
  ref?: Ref<HTMLInputElement>;
};

/**
 * Input that subscribes to its own field's error state via useFormState, avoiding
 * a parent re-render when unrelated field errors change. Works around FormField
 * (a shared component) subscribing to all errors via useFormContext().formState.errors.
 */
export const FormInput: FC<FormInputProps> = ({ ref, name, className, ...props }) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control, name });
  const fieldError = name.split('.').reduce<unknown>((obj, key) => (obj as Record<string, unknown>)?.[key], errors);
  return <input ref={ref} name={name} {...props} className={cn(className, { input__error: _notNil(fieldError) })} />;
};

/**
 * Shows one error message for a useFieldArray section. Per-field errors (e.g.
 * "Header key contains invalid characters") take priority over the array-level
 * root error (e.g. "At least one complete header is required").
 */
export const FieldArrayError: FC<{ name: string }> = ({ name }) => {
  const { control } = useFormContext();
  const { errors } = useFormState<FieldValues>({ control, name });
  const arrayErrors = name.split('.').reduce<unknown>((obj, key) => (obj as Record<string, unknown>)?.[key], errors) as
    | Record<string, unknown>
    | undefined;

  if (!_notNil(arrayErrors)) {
    return null;
  }

  const firstFieldMessage = Object.keys(arrayErrors).reduce<string | undefined>((found, k) => {
    if (_notNil(found) || k === 'root') {
      return found;
    }
    const entry = arrayErrors[k];
    if (!_notNil(entry) || typeof entry !== 'object') {
      return found;
    }
    for (const v of Object.values(entry as Record<string, unknown>)) {
      if (_notNil(v) && typeof v === 'object') {
        const msg = (v as { message?: string }).message;
        if (_notNil(msg)) {
          return msg;
        }
      }
    }
    return found;
  }, undefined);

  const rootMessage = (arrayErrors as { root?: { message?: string } }).root?.message;
  const message = firstFieldMessage ?? rootMessage;

  if (!_notNil(message)) {
    return null;
  }
  return (
    <small className="red db pt2" data-testid={`${name}-error`}>
      {message}
    </small>
  );
};
